import { useEffect, useRef, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import geocodeService, { type GeocodeResult } from '../../services/geocode';
import { placesEnabled } from '../../services/places';
import AddressAutocomplete from '../address/AddressAutocomplete';

interface AddressSearchProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (result: GeocodeResult) => void;
  placeholder?: string;
  label?: string;
}

function AddressSearch({
  value,
  onChange,
  onSelect,
  placeholder = 'Start typing an address…',
  label = 'Location / Address',
}: AddressSearchProps) {
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Google Places when it is set up; the OpenStreetMap search otherwise.
  // null until the server has said which.
  const [useGoogle, setUseGoogle] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    placesEnabled().then((on) => { if (!cancelled) setUseGoogle(on); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    const q = value.trim();
    if (useGoogle !== false || q.length < 3) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = window.setTimeout(async () => {
      try {
        const response = await geocodeService.search(q);
        if (response.success && response.data) {
          setResults(response.data);
          setOpen(true);
        }
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [value, useGoogle]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleSelect = (r: GeocodeResult) => {
    onChange(r.display_name);
    onSelect(r);
    setOpen(false);
  };

  if (useGoogle) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-neutral-700">{label}</label>
        <AddressAutocomplete
          inputProps={{ value, onChange: (e) => onChange(e.target.value) }}
          onPick={(a) => {
            const name = a.formatted ?? [a.line1, a.suburb, a.state, a.postcode].filter(Boolean).join(', ');
            onChange(name);
            if (a.lat != null && a.lng != null) {
              onSelect({ display_name: name, lat: a.lat, lon: a.lng, bbox: a.bbox, place: a });
            }
          }}
          className="input-professional w-full"
          placeholder={placeholder}
        />
        <p className="text-xs text-neutral-500">
          Select a result to recenter the map on that location.
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-2 relative">
      <label className="block text-sm font-semibold text-neutral-700">{label}</label>
      <div className="relative">
        <input
          className="input-professional w-full pr-9"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </div>
      </div>
      {open && results.length > 0 && (
        <ul className="absolute z-10 w-full bg-white border border-neutral-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {results.map((r, i) => (
            <li
              key={`${r.lat}-${r.lon}-${i}`}
              className="px-3 py-2 text-sm hover:bg-primary-50 cursor-pointer border-b border-neutral-100 last:border-b-0"
              onClick={() => handleSelect(r)}
            >
              {r.display_name}
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-neutral-500">
        Select a result to recenter the map on that location.
      </p>
    </div>
  );
}

export default AddressSearch;
